import React from "react";
import { useLocation } from "react-router-dom";

/**
 * RouteLoader
 * -----------
 * Full-viewport Midnight Forest overlay with the Crowther mark, played on
 * every route change (and on the very first mount).
 *
 * Two dismiss mechanisms run in parallel for reliability:
 *   1. CSS `animation-fill-mode: forwards` fades and hides the overlay.
 *   2. A React `useEffect` sets `visible = false` after the same window,
 *      guaranteeing unmount even under aggressive tab / animation throttling.
 *
 * The React `key={pathname}` prop remounts the component on every navigation
 * so the animation restarts cleanly, honouring the "loader on every page"
 * brief.
 */
export default function RouteLoader() {
  const { pathname } = useLocation();
  const [visible, setVisible] = React.useState(true);

  React.useEffect(() => {
    // Show on every route mount (initial + subsequent navigations).
    setVisible(true);
    let raf1;
    const start = performance.now();
    const tick = () => {
      const elapsed = performance.now() - start;
      if (elapsed >= 900) {
        setVisible(false);
        return;
      }
      raf1 = requestAnimationFrame(tick);
    };
    raf1 = requestAnimationFrame(tick);
    // Belt-and-braces timers so the loader can never stall.
    const t1 = setTimeout(() => setVisible(false), 1100);
    const t2 = setTimeout(() => setVisible(false), 2500);
    return () => {
      if (raf1) cancelAnimationFrame(raf1);
      clearTimeout(t1);
      clearTimeout(t2);
    };
  }, [pathname]);

  if (!visible) return null;

  const isDuplicate = /^\/(gateway-2|enterprise-2|machines-2|plan-2|login-2|onboarding)(\/|$)/.test(pathname);

  return (
    <div
      key={pathname}
      data-testid="route-loader"
      aria-hidden="true"
      className={`cw-route-loader${isDuplicate ? " is-teal" : ""}`}
    >
      <div className="cw-route-loader-doors" aria-hidden="true">
        <span className="cw-route-loader-door cw-route-loader-door-l" />
        <span className="cw-route-loader-door cw-route-loader-door-r" />
      </div>
      <div className="cw-route-loader-mark" aria-hidden="true">
        <img
          src="/logos/mark-black.png"
          alt=""
          width="72"
          height="72"
          draggable="false"
          onLoad={() => {
            // When the mark is decoded, start a final short hold, then hide.
            // Reliable regardless of tab throttling.
            const start = performance.now();
            const step = () => {
              const elapsed = performance.now() - start;
              if (elapsed >= 550) {
                setVisible(false);
                return;
              }
              requestAnimationFrame(step);
            };
            requestAnimationFrame(step);
            setTimeout(() => setVisible(false), 700);
          }}
        />
      </div>
    </div>
  );
}
